import moment from 'moment'
import { dollarFormatter } from './money'

const parseCurrencyDollars = (value) => {
  const numeric = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

const getDateInputFromDeliveryDecision = (decision = {}) => {
  if (decision?.status === 'manual-date' && decision?.dateInput) return decision.dateInput
  if (!['scheduled', 'available-now'].includes(decision?.status) || !decision?.label) return ''
  const parsed = moment(decision.label, ['MM/DD/YYYY', 'M/D/YYYY', 'M/D/YY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

export const parseAddressForShipTo = (address = '') => {
  const parts = String(address || '').split(',').map((part) => part.trim()).filter(Boolean)
  const street = parts[0] || ''
  const city = parts[1] || ''
  const stateZip = parts.slice(2).join(' ')
  const stateZipMatch = stateZip.match(/\b([A-Z]{2})\b\s*(\d{5}(?:-\d{4})?)?/i)

  return {
    name: 'Pasted quote address',
    shippingAddress: street,
    shippingCity: city,
    shippingState: stateZipMatch?.[1]?.toUpperCase() || '',
    shippingZip: stateZipMatch?.[2] || '',
  }
}

export const buildQuoteSourceNotes = ({
  fields = {},
  parsed = {},
  missingModels = [],
  totals = {},
  walkUpFloor = '',
  deliveryDecision = {},
  availabilityProjections = [],
  quoteNumber = '',
} = {}) => {
  const checkedAvailabilityProjections = (availabilityProjections || []).filter((projection) => projection.status !== 'not-checked')
  const walkUpBreakdown = Array.isArray(totals?.walkUpBreakdown) ? totals.walkUpBreakdown : []
  const walkUpBreakdownText = walkUpBreakdown.length
    ? walkUpBreakdown
        .map((row) => `${row.displayFloorLabel || row.floorLabel || 'Needs floor'}: ${row.quantity || 0} unit(s)${row.total ? ` = ${dollarFormatter.format(row.total)}` : ''}`)
        .join('; ')
    : ''
  const noteLines = [
    'Quote generated from quote builder.',
    quoteNumber ? `Quote number: ${quoteNumber}` : null,
    fields.address ? `Ship to address: ${fields.address}` : null,
    fields.dateAcquired ? `Quote date: ${moment(fields.dateAcquired).format('MM/DD/YYYY')}` : null,
    fields.dateRequired ? `Date required: ${fields.dateRequired}` : null,
    fields.reasonForPurchase ? `Reason for purchase: ${fields.reasonForPurchase}` : null,
    fields.deliveryNote ? `Delivery note: ${fields.deliveryNote}` : null,
    fields.walkUp ? `Walk up/Elevator: ${fields.walkUp}` : null,
    walkUpFloor ? `Walk up floor: ${walkUpFloor}` : null,
    totals?.walkUpTotal ? `Walk up charge: ${walkUpBreakdownText || `${dollarFormatter.format(totals.walkUpRate)} x ${totals.itemCount}`} = ${dollarFormatter.format(totals.walkUpTotal)}` : null,
    deliveryDecision?.label && !['No models yet', 'Run inventory lookup'].includes(deliveryDecision.label)
      ? `Earliest delivery based on inventory: ${deliveryDecision.label}`
      : null,
    checkedAvailabilityProjections.length
      ? `Inventory proof: ${checkedAvailabilityProjections.map((projection) => `${projection.modelNumber} requested ${projection.requestedQuantity}, target ${projection.targetQuantity}, available ${projection.currentAvailable ?? 'N/A'}, earliest ${projection.earliestLabel}`).join('; ')}`
      : null,
    parsed.subtotal ? `Source subtotal: ${dollarFormatter.format(parsed.subtotal)}` : null,
    missingModels.length ? `Products not found in catalog: ${missingModels.join(', ')}` : null,
  ]

  return noteLines.filter(Boolean).join('\n')
}

const normalizeWalkUpQuantity = (value) => {
  const quantity = Number(value)
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0
}

export const buildWalkUpChargesFromTotals = (totals = {}) => {
  const breakdown = Array.isArray(totals?.walkUpBreakdown) ? totals.walkUpBreakdown : []
  return breakdown
    .filter((row) => Number(row?.total) > 0 && !row?.needsFallback)
    .map((row) => {
      const walkUp = row.floorLabel || row.displayFloorLabel || ''
      const quantity = normalizeWalkUpQuantity(row.quantity)
      const rate = Number(row.rate) || 0
      const total = Number(row.total) || rate * quantity
      return {
        walkUp,
        label: walkUp,
        quantity,
        rate,
        total,
      }
    })
    .filter((row) => row.walkUp && row.quantity > 0)
}

export const buildInvoiceDraftFromQuote = (quote = {}) => {
  const fields = quote.fields || {}
  const totals = quote.totals || {}
  const deliveryFee = Number(totals.deliveryFee) || parseCurrencyDollars(fields.deliveryFee)
  const walkUpCharges = buildWalkUpChargesFromTotals(totals)
  const requiresWalkUp = Boolean(quote.walkUpFloor || totals.walkUpTotal)
  const walkUpTotal = walkUpCharges.length
    ? walkUpCharges.reduce((sum, charge) => sum + (Number(charge.total) || 0), 0)
    : Number(totals.walkUpTotal) || 0
  const walkUpQuantity = walkUpCharges.length
    ? walkUpCharges.reduce((sum, charge) => sum + (Number(charge.quantity) || 0), 0)
    : (requiresWalkUp && totals.walkUpTotal ? totals.itemCount || 1 : 1)
  const walkUpLabel = walkUpCharges.length === 1
    ? walkUpCharges[0].walkUp
    : walkUpCharges.length > 1
      ? 'Multiple walk-ups'
      : totals.walkUpLabel || quote.walkUpFloor || (totals.walkUpTotal ? 'Multiple walk-ups' : '')
  const shipTo = fields.address ? parseAddressForShipTo(fields.address) : null
  const receivedByDateInput = fields.earliestFreeDeliveryDate || getDateInputFromDeliveryDecision(quote.deliveryDecision) || fields.dateRequiredInput
  const receivedByDate = receivedByDateInput
    ? moment(receivedByDateInput).toISOString()
    : undefined

  return {
    quoteId: quote._id,
    quoteNumber: quote.quoteNumber,
    source: 'saved-quote',
    type: 'Quotation',
    status: 'Unpaid',
    shipTo,
    receivedByDate,
    notes: buildQuoteSourceNotes({
      fields,
      parsed: {},
      missingModels: (quote.items || []).filter((item) => item.matchStatus === 'missing').map((item) => item.modelNumber),
      totals,
      walkUpFloor: quote.walkUpFloor,
      deliveryDecision: quote.deliveryDecision,
      availabilityProjections: quote.availabilityProjections,
      quoteNumber: quote.quoteNumber,
    }),
    walkUp: requiresWalkUp ? walkUpLabel : '',
    walkUpCharges,
    walkUpQuantity,
    walkupHaulawayUnits: walkUpQuantity,
    walkUpFee: requiresWalkUp ? walkUpTotal : 0,
    deliveryFee,
    deliveryFeeOverride: deliveryFee > 0,
    items: (quote.items || []).map((line) => ({
      modelNumber: line.modelNumber,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      unitCost: line.unitCost,
      discount: '',
      amount: '',
      doNotOrder: false,
      lockup: false,
    })),
  }
}
